import { ApiError } from "@groundspark/browser-auth";

export interface ElectionCandidate {
  readonly candidateId: number;
  readonly candidateName: string;
  readonly partyName: string;
  readonly voteCount: number;
}

export interface ConstituencyElectionResult {
  readonly constituencyCode: string;
  readonly constituencyName: string;
  readonly electionDate: string;
  readonly isByElection: boolean;
  readonly validVoteCount: number;
  readonly electorateCount: number;
  readonly candidates: readonly ElectionCandidate[];
}

export function parseConstituencyResults(
  value: unknown,
): readonly ConstituencyElectionResult[] {
  if (!Array.isArray(value) || value.length === 0)
    throw new ApiError("Missing constituency results.");
  const seen = new Set<string>();
  const candidateIds = new Set<number>();
  return value.map((item: unknown) => {
    const row = record(item);
    const code = nonemptyString(row.constituency_code);
    const electionDate = nonemptyString(row.election_date);
    const validVoteCount = count(row.valid_vote_count);
    const electorateCount = count(row.electorate_count);
    if (
      !/^[EWNS]\d{8}$/.test(code) ||
      seen.has(code) ||
      !/^\d{4}-\d{2}-\d{2}$/.test(electionDate) ||
      !Number.isFinite(Date.parse(electionDate)) ||
      new Date(electionDate).toISOString().slice(0, 10) !== electionDate ||
      typeof row.is_by_election !== "boolean" ||
      validVoteCount === 0 ||
      validVoteCount > electorateCount ||
      !Array.isArray(row.candidates) ||
      row.candidates.length === 0
    )
      throw new ApiError("Invalid constituency election metadata.");
    seen.add(code);
    const candidates = row.candidates
      .map((item: unknown): ElectionCandidate => {
        const candidate = record(item);
        const candidateId = count(candidate.candidate_id);
        if (candidateIds.has(candidateId)) throw new ApiError("Duplicate election candidate.");
        candidateIds.add(candidateId);
        return {
          candidateId,
          candidateName: nonemptyString(candidate.candidate_name),
          partyName: nonemptyString(candidate.party_name),
          voteCount: count(candidate.vote_count),
        };
      })
      .sort((a, b) => b.voteCount - a.voteCount || a.candidateId - b.candidateId);
    if (
      candidates.reduce((sum, candidate) => sum + candidate.voteCount, 0) !== validVoteCount
    ) {
      throw new ApiError("Candidate votes do not equal the constituency valid-vote total.");
    }
    return {
      constituencyCode: code,
      constituencyName: nonemptyString(row.constituency_name),
      electionDate,
      isByElection: row.is_by_election,
      validVoteCount,
      electorateCount,
      candidates,
    };
  });
}

export function renderConstituencyResult(
  container: HTMLElement,
  result: ConstituencyElectionResult,
): void {
  container.innerHTML = `
    <p class="election-area" data-election-name></p>
    <p class="election-context" data-election-date></p>
    <p class="election-context">Share of electorate · (none) includes nonvoters and rejected ballots</p>
    <ol class="vote-shares" aria-label="Shares of electorate"></ol>`;
  required(container, "[data-election-name]").textContent = result.constituencyName;
  required(container, "[data-election-date]").textContent =
    `${result.isByElection ? "By-election" : "General election"} · ${formatElectionDate(result.electionDate)}`;
  renderVoteShares(required(container, ".vote-shares"), result.candidates, {
    electorateCount: result.electorateCount,
    noValidVoteCount: result.electorateCount - result.validVoteCount,
  });
}

export function formatElectionDate(date: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(date));
}

export function renderVoteShares(
  list: HTMLElement,
  candidates: readonly Pick<ElectionCandidate, "candidateName" | "partyName" | "voteCount">[],
  participation: {
    readonly electorateCount: number | null;
    readonly noValidVoteCount: number | null;
    readonly estimated?: boolean;
  },
): void {
  const total =
    participation.electorateCount ??
    candidates.reduce((sum, candidate) => sum + candidate.voteCount, 0);
  const rows = [
    ...candidates.map((candidate) => ({ ...candidate, unit: "votes" })),
    {
      partyName: "(none)",
      candidateName: "",
      voteCount: participation.noValidVoteCount,
      unit: "people",
    },
  ].sort((a, b) => (b.voteCount ?? -1) - (a.voteCount ?? -1));
  for (const candidate of rows) {
    const share =
      candidate.voteCount === null ? null : total > 0 ? (candidate.voteCount / total) * 100 : 0;
    const item = document.createElement("li");
    item.innerHTML = `<div class="vote-share-heading"><strong data-party></strong><strong data-share></strong></div><div class="vote-share-track" aria-hidden="true"><div class="vote-share-bar"></div></div><div class="vote-share-detail"><span data-candidate></span><span data-votes></span></div>`;
    required(item, "[data-party]").textContent = candidate.partyName;
    required(item, "[data-share]").textContent =
      share === null ? "Unavailable" : `${share.toFixed(1)}%`;
    const name = required(item, "[data-candidate]");
    if (candidate.candidateName) name.textContent = candidate.candidateName;
    else name.remove();
    required(item, "[data-votes]").textContent =
      candidate.voteCount === null
        ? "Insufficient turnout data"
        : `${candidate.voteCount.toLocaleString("en-GB", { maximumFractionDigits: participation.estimated ? 1 : 0 })} ${candidate.unit}`;
    const track = required(item, ".vote-share-track");
    if (share === null) track.remove();
    else required(item, ".vote-share-bar").style.width = `${Math.min(100, share)}%`;
    list.append(item);
  }
}

function required(container: HTMLElement, selector: string): HTMLElement {
  const element = container.querySelector<HTMLElement>(selector);
  if (element === null) throw new Error(`Missing election element ${selector}`);
  return element;
}

function record(value: unknown): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value))
    throw new ApiError("Invalid election result.");
  return value as Record<string, unknown>;
}

function nonemptyString(value: unknown): string {
  if (typeof value !== "string" || value.trim().length === 0)
    throw new ApiError("Missing election result text.");
  return value;
}

function count(value: unknown): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0)
    throw new ApiError("Invalid election count.");
  return value;
}
